import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUiStore } from "@/stores/ui";
import { useTasksStore } from "@/stores/tasks";
import { TaskDraft } from "@/core/state-shape";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";

const TaskNewPage = () => {
  const navigate = useNavigate();
  const setHeaderTitle = useUiStore((state) => state.setHeaderTitle);
  const { drafts, activeDraftId, create: createTaskAction, patch: patchTask } = useTasksStore();
  const draft = activeDraftId ? drafts[activeDraftId] as TaskDraft | undefined : undefined;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setHeaderTitle("New Task");
  }, [setHeaderTitle]);

  const instruction = draft?.instruction ?? "";
  const scopes = draft?.scope ?? [];
  const intent = draft?.intent ?? "Add Feature";
  const constraints = draft?.constraints ?? {};

  const updateDraft = (updates: Partial<TaskDraft>) => {
    if (!activeDraftId) return;
    patchTask({
      drafts: {
        ...drafts,
        [activeDraftId]: { ...(draft ?? { scope: [], instruction: '' }), ...updates },
      },
    });
  };
  
  const updateConstraint = (key: string, value: string) => {
    updateDraft({
        constraints: {
            ...constraints,
            [key]: value,
        }
    });
  };

  const removeScope = (indexToRemove: number) => {
    updateDraft({ scope: scopes.filter((_, index) => index !== indexToRemove) });
  };

  const handleSubmit = async () => {
    if (!instruction.trim() || instruction.trim().length < 15) {
      setError("Instruction must be at least 15 characters.");
      return;
    }
    if (scopes.length === 0) {
      setError("Please add at least one file or selection to the scope from the Workspace.");
      return;
    }
    if (!activeDraftId) {
      setError("No active draft found. Please go back to the workspace.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const latestDraft = useTasksStore.getState().drafts[activeDraftId];
      if (!latestDraft) {
          throw new Error("Draft not found.");
      }
      const taskId = await createTaskAction(latestDraft, activeDraftId);

      navigate(`/pages/progress/?taskId=${taskId}`);
    } catch (e: any) {
      setError(e.message || "Failed to create task. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <header className="mb-4 hidden md:block">
        <h1 className="text-2xl font-bold ">New Task</h1>
        <p className="text-muted-foreground">Draft and submit a precise request for the system to act on.</p>
      </header>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Scope</CardTitle>
            <CardDescription>Files and selections this task should focus on. Add scope from the Workspace page.</CardDescription>
          </CardHeader>
          <CardContent>
            {scopes.length > 0 ? (
              <ul className="flex flex-wrap gap-2">
                {scopes.map((s, i) => (
                  <li key={`${(s as any).path}-${i}`} className="flex items-center gap-1 bg-secondary text-secondary-foreground rounded-md pl-2 pr-1 text-sm">
                    {s.type === "file" ? (
                      <span title={s.path}>📄 {s.path}</span>
                    ) : (
                      <span title={`${s.path}:${(s as any).startLine}-${(s as any).endLine}`}>
                        📄 {s.path}:{(s as any).startLine}-{(s as any).endLine}
                      </span>
                    )}
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeScope(i)}>
                      <X className="h-3 w-3" />
                      <span className="sr-only">Remove scope</span>
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No scope defined. Go to the Workspace to add files or code selections.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Intent</CardTitle>
            <CardDescription>What is the primary goal of this task?</CardDescription>
          </CardHeader>
          <CardContent>
            <ToggleGroup
              type="single"
              value={intent}
              onValueChange={(value) => { if (value) updateDraft({ intent: value as TaskDraft['intent'] }) }}
              className="flex-wrap justify-start"
            >
              <ToggleGroupItem value="Add Feature">Add Feature</ToggleGroupItem>
              <ToggleGroupItem value="Fix Bug">Fix Bug</ToggleGroupItem>
              <ToggleGroupItem value="Refactor">Refactor</ToggleGroupItem>
              <ToggleGroupItem value="Add Tests">Add Tests</ToggleGroupItem>
              <ToggleGroupItem value="Update Docs">Update Docs</ToggleGroupItem>
            </ToggleGroup>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Instruction</CardTitle>
            <CardDescription>Describe the desired change, why it's needed, and what success looks like.</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="e.g., Implement the login button functionality in Login.tsx. It should call the auth service and handle both success and error states..."
              value={instruction}
              onChange={(e) => updateDraft({ instruction: e.target.value })}
              className="min-h-[150px]"
            />
            <p className="text-sm text-muted-foreground mt-2">
              Character count: {instruction.length} (min 15 required)
            </p>
          </CardContent>
        </Card>

        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="constraints">
            <AccordionTrigger>Advanced Constraints (Optional)</AccordionTrigger>
            <AccordionContent className="space-y-4 p-1 pt-4">
               <div className="grid gap-1.5">
                  <Label htmlFor="boundaries">Boundaries</Label>
                  <Input 
                    id="boundaries" 
                    placeholder="e.g., Only modify files in src/components/auth"
                    value={(constraints as any).boundaries || ""}
                    onChange={(e) => updateConstraint('boundaries', e.target.value)}
                   />
                  <p className="text-sm text-muted-foreground">Specify files or patterns to restrict changes to.</p>
               </div>
               <div className="grid gap-1.5">
                  <Label htmlFor="non-goals">Non-Goals</Label>
                  <Input 
                    id="non-goals"
                    placeholder="e.g., Do not refactor the styling system"
                    value={(constraints as any).nonGoals || ""}
                    onChange={(e) => updateConstraint('nonGoals', e.target.value)}
                  />
                  <p className="text-sm text-muted-foreground">Clearly state what should not be changed.</p>
               </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Card>
          <CardFooter className="flex justify-between items-center flex-wrap gap-2">
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex-grow"></div> {/* Spacer */}
            <div className="flex gap-2">
                <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={loading || scopes.length === 0 || instruction.length < 15}>
                  {loading ? "Generating..." : "Generate"}
                </Button>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default TaskNewPage;